// notices/AddNotice.tsx

import { getMasterDropdownService } from "@/services/masters";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export const noticeSchema = z.object({
  Message: z
    .string()
    .trim()
    .min(5, "Notice message must be at least 5 characters")
    .max(500, "Notice message cannot exceed 500 characters"),

  Visible: z
    .array(z.string())
    .min(1, "Please select at least one role"),
});

export type NoticeFormData = z.infer<typeof noticeSchema>;

interface Role {
  id: number;
  Name: string;
}

export default function AddNoticeForm() {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<NoticeFormData>();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const selectedRoles = watch("Visible") || [];

  useEffect(() => {
    register("Visible");
  }, [register]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);

      const res = await getMasterDropdownService();

      if (res?.status === 200) {
        const raw = res.data as any;

        const apiRoles = raw?.Designations || [];

        setRoles([
          {
            id: 6,
            Name: "Parent",
          },
          ...apiRoles,
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  const toggleRole = (roleName: string) => {
    const updated = selectedRoles.includes(roleName)
      ? selectedRoles.filter((r) => r !== roleName)
      : [...selectedRoles, roleName];

    setValue("Visible", updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <div className="space-y-5">
      {/* Notice Message */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notice Message <span className="text-red-500">*</span>
        </label>

        <textarea
          {...register("Message")}
          rows={5}
          placeholder="Enter notice message"
          className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${errors.Message
              ? "border-red-500 focus:ring-red-200"
              : "border-gray-300 focus:ring-blue-200"
            }`}
        />

        {errors.Message && (
          <p className="mt-1 text-sm text-red-500">
            {errors.Message.message}
          </p>
        )}
      </div>

      {/* Visible To */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Visible To <span className="text-red-500">*</span>
        </label>

        {loadingRoles ? (
          <div className="text-sm text-gray-500">
            Loading roles...
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {roles.map((role) => (
              <label
                key={role.id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role.Name)}
                  onChange={() => toggleRole(role.Name)}
                  className="h-4 w-4"
                />

                <span className="text-sm text-gray-700">
                  {role.Name}
                </span>
              </label>
            ))}
          </div>
        )}

        {errors.Visible && (
          <p className="mt-1 text-sm text-red-500">
            {errors.Visible.message}
          </p>
        )}
      </div>

      {/* Selected Roles Preview */}
      {selectedRoles.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Selected Roles:
          </p>

          <div className="flex flex-wrap gap-2">
            {selectedRoles.map((role) => (
              <span
                key={role}
                className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}